import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Search,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Home,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslation } from '../context/TranslationContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { APP_LOGO_SRC } from '../config/branding';
import { checkNijBegunSubsidie } from '../lib/nijBegunSubsidie';

const RESULT_STYLES = {
  full: {
    icon: CheckCircle2,
    card: 'border-green-200 bg-green-50',
    iconClass: 'text-green-600',
    badge: 'bg-green-100 text-green-800 border-green-200',
  },
  partial: {
    icon: AlertTriangle,
    card: 'border-amber-200 bg-amber-50',
    iconClass: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  outside_region: {
    icon: XCircle,
    card: 'border-red-200 bg-red-50',
    iconClass: 'text-red-600',
    badge: 'bg-red-100 text-red-800 border-red-200',
  },
  invalid_postcode: {
    icon: XCircle,
    card: 'border-red-200 bg-red-50',
    iconClass: 'text-red-600',
    badge: 'bg-red-100 text-red-800 border-red-200',
  },
  missing_huisnummer: {
    icon: XCircle,
    card: 'border-red-200 bg-red-50',
    iconClass: 'text-red-600',
    badge: 'bg-red-100 text-red-800 border-red-200',
  },
};

const SubsidieCheckerPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [postcode, setPostcode] = useState('');
  const [huisnummer, setHuisnummer] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCheck = async (event) => {
    event.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const outcome = await checkNijBegunSubsidie(postcode, huisnummer);
      setResult(outcome);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestQuote = () => {
    navigate('/configurator-select', { state: { newQuote: true } });
  };

  const resultStyle = result ? RESULT_STYLES[result.status] : null;
  const ResultIcon = resultStyle?.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      <nav className="border-b border-gray-200 bg-white/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <img src={APP_LOGO_SRC} alt="config-go" className="h-10 w-auto object-contain" />
            </button>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('subsidie.backToHome')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <header className="border-b border-orange-100 bg-gradient-to-r from-orange-600 to-orange-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Badge className="mb-4 bg-white/20 text-white border-white/30 hover:bg-white/20">
            {t('subsidie.badge')}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('subsidie.title')}</h1>
          <p className="text-lg text-orange-100 max-w-2xl">{t('subsidie.subtitle')}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-6">
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-orange-600" />
                  {t('subsidie.formTitle')}
                </CardTitle>
                <CardDescription>{t('subsidie.formDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCheck} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="postcode">{t('subsidie.postcodeLabel')}</Label>
                    <Input
                      id="postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      placeholder={t('subsidie.postcodePlaceholder')}
                      autoComplete="postal-code"
                      className="text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="huisnummer">{t('subsidie.huisnummerLabel')}</Label>
                    <Input
                      id="huisnummer"
                      value={huisnummer}
                      onChange={(e) => setHuisnummer(e.target.value)}
                      placeholder={t('subsidie.huisnummerPlaceholder')}
                      autoComplete="address-line1"
                      className="text-base"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-base"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {t('subsidie.checking')}
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-5 w-5" />
                        {t('subsidie.checkButton')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {result?.bagAddress && (
              <Card className="border border-blue-200 bg-blue-50/80">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-blue-900">
                    <MapPin className="h-4 w-4" />
                    {t('subsidie.addressFound')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-blue-900/90 space-y-1">
                  <p className="font-medium">{result.bagAddress.weergavenaam}</p>
                  <p>
                    {t('subsidie.municipality')}: {result.bagAddress.gemeentenaam || '—'}
                  </p>
                  <p>
                    {t('subsidie.province')}: {result.bagAddress.provincienaam || '—'}
                  </p>
                  <p className="text-xs text-blue-700/80 pt-1">
                    BAG ID: {result.bagAddress.adresseerbaarobject_id || result.bagAddress.id || '—'}
                  </p>
                </CardContent>
              </Card>
            )}

            {result && resultStyle && ResultIcon && (
              <Card className={`border-2 shadow-md ${resultStyle.card}`}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <ResultIcon className={`h-8 w-8 shrink-0 ${resultStyle.iconClass}`} />
                    <div>
                      <Badge className={`mb-2 ${resultStyle.badge}`}>
                        {t(`subsidie.result.${result.status}.badge`)}
                      </Badge>
                      <CardTitle className="text-xl text-gray-900">
                        {t(`subsidie.result.${result.status}.title`)}
                      </CardTitle>
                      <CardDescription className="text-base mt-2 text-gray-700">
                        {t(`subsidie.result.${result.status}.description`)}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                {(result.status === 'full' || result.status === 'partial') && (
                  <CardContent className="pt-0">
                    <Button
                      onClick={handleRequestQuote}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      {t('subsidie.requestQuote')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                )}
                {result.bagError && (
                  <CardContent className="pt-0">
                    <p className="text-sm text-gray-600">
                      {t('subsidie.bagWarning')}: {result.bagError}
                    </p>
                  </CardContent>
                )}
              </Card>
            )}
          </div>

          <aside className="lg:col-span-2 space-y-4">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Info className="h-5 w-5 text-orange-600" />
                  {t('subsidie.howItWorksTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-600">
                <div className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-bold">100%</span>
                  <p>{t('subsidie.tierFull')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs font-bold">50%</span>
                  <p>{t('subsidie.tierPartial')}</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700 text-xs font-bold">0%</span>
                  <p>{t('subsidie.tierNone')}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-slate-50">
              <CardContent className="pt-6 text-sm text-gray-600 leading-relaxed">
                <p className="flex gap-2">
                  <Home className="h-4 w-4 shrink-0 text-orange-600 mt-0.5" />
                  {t('subsidie.disclaimer')}
                </p>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-8 mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-gray-500">
          {t('subsidie.footer')}
        </div>
      </footer>
    </div>
  );
};

export default SubsidieCheckerPage;
